import React from 'react';
import { BookOpen, Users, Wrench, FileText, HelpCircle, ArrowRight } from 'lucide-react';

interface ResourceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  link: string;
  image: string;
  count?: string;
}

// Instant jump with subtle element animation - NO SCROLL ANIMATION
const smoothScrollTo = (elementId: string, offset: number = 80) => {
  const element = document.getElementById(elementId);
  if (!element) return;
  
  const elementPosition = element.getBoundingClientRect().top;
  const targetPosition = window.pageYOffset + elementPosition - offset;
  
  // Instantly jump to the target position - NO ANIMATION
  window.scrollTo({
    top: targetPosition,
    behavior: 'instant' as ScrollBehavior
  });
  
  // Small delay to ensure scroll is complete before element animation
  setTimeout(() => {
    // Add subtle animation effect to the target element
    element.style.opacity = '0';
    element.style.transform = 'translateY(20px)';
    element.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
    
    // Trigger animation
    requestAnimationFrame(() => {
      element.style.opacity = '1';
      element.style.transform = 'translateY(0)';
    });
    
    // Clean up after animation
    setTimeout(() => {
      element.style.transition = '';
    }, 600);
  }, 10);
};

const ResourceCard: React.FC<ResourceCardProps> = ({
  icon,
  title,
  description,
  link,
  image,
  count,
}) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const targetId = link.replace('#', '');
    smoothScrollTo(targetId);
  };

  return (
    <a
      href={link}
      onClick={handleClick}
      className="group block bg-white dark:bg-slate-800 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-slate-700 overflow-hidden"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
        <div className="absolute top-4 right-4">
          <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
            <div className="text-white">
              {icon}
            </div>
          </div>
        </div>
        {count && (
          <div className="absolute bottom-4 left-4">
            <span className="px-3 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold rounded-full">
              {count}
            </span>
          </div>
        )}
      </div>
      <div className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
          {title}
        </h3>
        <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
          {description}
        </p>
        <div className="flex items-center text-purple-600 dark:text-purple-400 font-medium group-hover:gap-2 transition-all">
          <span>Explore</span>
          <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </a>
  );
};

const ResourcesSection: React.FC = () => {
  const resources = [
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: 'Blog',
      description: 'Uncover meaningful perspectives to enhance your organizational wellness initiative and remain updated with health trends. Professional pieces on staff wellness, exercise advice, psychological health tactics, initiative administration optimal methods, and sector investigations. Regular publications addressing subjects from dietary science to office ergonomics. Success narratives, instructional materials, and expert opinions from wellness sector specialists. Follow to remain knowledgeable about current developments, study outcomes, and creative methods to organizational wellness.',
      link: '#blog',
      image: 'https://images.unsplash.com/photo-1486312338219-ce68e2c6f44d?w=600&h=400&fit=crop',
      count: '50+ Articles',
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Client Success Stories',
      description: 'Explore how Fitzaa enables organizations worldwide to achieve their staff wellness goals. Authentic narratives from businesses of various scales sharing their wellness path, obstacles conquered, and outcomes realized. Gain insights from major corporations, emerging companies, medical institutions, and public sector entities. Thorough success examples with measurements, return-on-investment information, and endorsements. Comprehend how various sectors deploy wellness initiatives and which approaches prove most effective. Draw motivation from achievement narratives and gain knowledge from others\' journeys.',
      link: '#success-stories',
      image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=600&h=400&fit=crop',
      count: '100+ Stories',
    },
    {
      icon: <Wrench className="w-6 h-6" />,
      title: 'Tools and Templates',
      description: 'Reach immediately available materials and adaptable utilities for your wellness initiative. Competition formats, messaging assets, incentive initiative structures, and evaluation instruments. Obtainable wellness schedules, objective-establishment documents, and initiative preparation formats. Electronic mail formats for notifications and communications. Social platform visuals and marketing assets. Questionnaire formats for initiative assessment. Financial calculators and return-on-investment analyzers. Adaptable materials that conserve time and guarantee expert initiative implementation.',
      link: '#tools',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop',
      count: '25+ Tools',
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'Guides',
      description: 'Personalized materials for effective wellness initiative deployment, offering sequential instructions. Thorough manuals addressing initiative introduction, competition creation, participation approaches, and initiative enhancement. Deployment checklists, optimal methods, frequent errors to prevent, and problem-solving suggestions. Sector-focused manuals for medical care, information technology, production, and additional fields. Manuals for various initiative categories from step competitions to complete wellness projects. Specialist-authored materials grounded in extensive experience assisting organizations in achieving success.',
      link: '#guides',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=400&fit=crop',
      count: '15+ Guides',
    },
    {
      icon: <HelpCircle className="w-6 h-6" />,
      title: 'Help Center',
      description: 'Obtain responses to your inquiries and discover additional information about Fitzaa. Thorough documentation and assistance. Browseable information repository with pieces, instructional materials, and frequently asked questions. Visual tutorials for routine activities, problem-solving suggestions, and functionality descriptions. Real-time messaging assistance during operating hours, electronic mail support, and arranged consultations. User discussion boards for community assistance and collaborative learning. Periodic online seminars and educational sessions. All essentials required to maximize Fitzaa and guarantee your wellness initiative thrives.',
      link: '#help',
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop',
    },
  ];

  const featuredBlogPosts = [
    {
      title: 'Creating Effective Step Competitions for Your Organization',
      description: 'Workplace step competitions serve as a valuable organizational wellness instrument that companies can deploy to encourage health and wellness among employees.',
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=250&fit=crop',
      date: 'Jan 15, 2025',
    },
    {
      title: 'Staff Wellness: A Critical Element for Organizational Performance',
      description: 'Understand how staff wellness directly influences performance and explore approaches to enhance both areas.',
      image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400&h=250&fit=crop',
      date: 'Jan 10, 2025',
    },
    {
      title: 'Essential Advantages of Staff Wellness Initiatives',
      description: 'Examine the primary advantages that wellness initiatives deliver to organizations and their workforce.',
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=250&fit=crop',
      date: 'Jan 5, 2025',
    },
  ];

  return (
    <section id="resources" className="py-20 bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Tools to Support Your Success
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            All the resources required to develop and sustain an effective wellness initiative
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {resources.map((resource, index) => (
            <div key={index} id={resource.link.replace('#', '')} className="scroll-mt-20">
              <ResourceCard {...resource} />
            </div>
          ))}
        </div>

        <div className="mt-16">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
              Wellness Articles & Insights
            </h3>
            <a
              href="#blog"
              className="text-purple-600 dark:text-purple-400 font-semibold hover:text-purple-700 dark:hover:text-purple-300 flex items-center"
            >
              View All
              <ArrowRight className="ml-2 w-4 h-4" />
            </a>
          </div>
          <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-3xl">
            Gain access to expert insights and reliable information on employee well-being, corporate wellness, and health from our trusted professionals.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredBlogPosts.map((post, index) => (
              <a
                key={index}
                href="#blog"
                className="group block bg-white dark:bg-slate-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200 dark:border-slate-700 overflow-hidden"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
                <div className="p-6">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{post.date}</p>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors line-clamp-2">
                    {post.title}
                  </h4>
                  <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-3">
                    {post.description}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResourcesSection;

